import { FC } from "react";
import { ConflictDialog } from "../ConflictDialog/ConflictDialog";
import { BlockerDialog } from "../BlockerDialog/BlockerDialog";
import { WorkingHoursDialog } from "../WorkingHoursDialog/WorkingHoursDialog";
import type { ScheduleConflictFlow } from "../../../hooks/useScheduleConflictFlow";

/**
 * The three dialogs a `useScheduleConflictFlow` can open, wired to it in one place.
 *
 * Render it as a **sibling** of the owning modal, not a child: every modal shares z-index 100, so
 * the one later in the DOM wins. A hidden container is `pointer-events: none`, so these cost
 * nothing while closed.
 *
 * Every copy prop is optional — the dialogs carry sensible defaults — but the sentence differs per
 * owner ("den Termin" vs "die Aufgabe"), so callers generally pass their own.
 */
interface Props {
  flow: ScheduleConflictFlow;
  /** Disables the confirm buttons while the retry is in flight. */
  isLoading?: boolean;
  /** Where the blockers dialog sends the user to free up the range — usually Umdisponierung. */
  onGoToReschedule: () => void;
  conflictTitle?: string;
  conflictDescription?: string;
  conflictConfirmLabel?: string;
  workingHoursDescription?: string;
  blockersDescription?: string;
}

export const ScheduleConflictDialogs: FC<Props> = ({
  flow,
  isLoading = false,
  onGoToReschedule,
  conflictTitle,
  conflictDescription,
  conflictConfirmLabel,
  workingHoursDescription,
  blockersDescription,
}) => (
  <>
    <ConflictDialog
      open={flow.conflicts !== null}
      conflicts={flow.conflicts ?? []}
      title={conflictTitle}
      description={conflictDescription}
      confirmLabel={conflictConfirmLabel}
      onConfirm={flow.confirmConflict}
      // Cancel leaves the form open with everything still filled in, so the user can pick
      // another slot instead of starting over.
      onCancel={flow.dismiss}
      isLoading={isLoading}
    />

    <WorkingHoursDialog
      open={flow.violations !== null}
      violations={flow.violations ?? []}
      description={workingHoursDescription}
      // Keeps the dialog's own "Trotzdem planen" rather than mirroring the conflict dialog's
      // wording: the two can appear one after the other on the same slot, and identical buttons
      // would read as the same question asked twice.
      onConfirm={flow.confirmWorkingHours}
      onCancel={flow.dismiss}
      isLoading={isLoading}
    />

    <BlockerDialog
      open={flow.blockers !== null}
      blockers={flow.blockers ?? []}
      description={blockersDescription}
      onClose={flow.closeBlockers}
      onGoToReschedule={onGoToReschedule}
    />
  </>
);
